import { Injectable } from '@angular/core';
import { SessionCookieService, UtilService } from '@docpath/linces-angular-components';
import { DpFieldSelect, DpFieldTable, DpFieldTableGroup, DpFieldText, DpFormButton, DpFormButtonType, DpFormRow, DpModalForm, DpModalFormService, DpModalFormSize, DpSelectOption, DpTableGroupColumn } from '@docpath/webcomponents';
import { Table, TableField, TableFieldDate, TableFieldText, TableRow } from '@docpath/webcomponents/table';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from 'src/app/constants/constants';
import { CloudService } from 'src/app/shared/services/cloud.service';

import { StatisticsTableService } from '../components/table/statistics-table.service';
import { Format, Job, JobConfiguration } from '../models/job';
import { StatisticsService } from './statistics.service';
import { StatisticsHttpService } from './statistics-http.service';

@Injectable({
    providedIn: 'root'
})
export class StatisticsFormService {

    readonly i18nPrefixRelaunch = 'statistics.relaunch-form';
    readonly i18nPrefixRelaunched = 'statistics.relaunched-form';

    // eslint-disable-next-line max-params
    constructor(
        private readonly modalFormService: DpModalFormService,
        private readonly statisticsHttpService: StatisticsHttpService,
        private readonly statisticsService: StatisticsService,
        private readonly utilService: UtilService,
        private readonly statisticsTableService: StatisticsTableService,
        private readonly translateService: TranslateService,
        private readonly cloudService: CloudService,
        private readonly sessionCookieService: SessionCookieService
    ) {
    }

    openRelaunchJobForm(jobConfiguration: JobConfiguration): void {
        const form = this.initLaunchJobForm();
        form.header = `${this.i18nPrefixRelaunch}.header.relaunch-job`;

        form.submit = (value, activeModal) => {
            const newJobConfiguration = this.getNewJobConfiguration(jobConfiguration, value, value.formatValue);
            this.relaunchJob(newJobConfiguration, activeModal);
        };

        const modalReference = this.modalFormService.open(form);

        modalReference.shown.subscribe(() => {
            let formValue;
            const { format } = jobConfiguration;
            if (!Object.values(Format).includes(format as Format)) {
                formValue = { formatValue: format, ...jobConfiguration, format: Format.custom };
                form.formGroup?.get('formatValue')?.enable();
            } else {
                formValue = jobConfiguration;
            }

            form.setValue(formValue);
            this.onChangeFormat(form);
        });
    }

    openViewRelaunchedJobsForm(jobId: number, relaunchedJobs: Job[]): void {
        const form = this.initViewLaunchedJobsForm(relaunchedJobs);
        form.header = this.loadHeaderTitle(jobId);

        this.translateService.onLangChange.subscribe(() => {
            form.header = this.loadHeaderTitle(jobId);
        });

        const modalReference = this.modalFormService.open(form);
        modalReference.shown.subscribe();

        const cancelButton = form.buttons.find(button => button.type === DpFormButtonType.Cancel);
        if (cancelButton) {
            cancelButton.onClick = () => modalReference.close();
        }
    }

    private initLaunchJobForm(): DpModalForm {
        return new DpModalForm({
            header: `${this.i18nPrefixRelaunch}.header.relaunch-job`,
            rows: [
                new DpFormRow([
                    this.getFormatField(),
                    new DpFieldText({ name: 'formatValue', column: 5, disabled: true, labelHidden: true })
                ]),
                new DpFormRow([
                    new DpFieldText({ name: 'project', column: 10 })
                ]),
                new DpFormRow([
                    new DpFieldText({ name: 'output', column: 10, required: true })
                ]),
                this.getParametersFields()
            ],
            size: DpModalFormSize.Lg,
            i18nPrefix: this.i18nPrefixRelaunch,
            buttons: this.getRelaunchJobFormButtons(),
            allowUnchangedSubmit: true
        });
    }

    private getNewJobConfiguration(jobConfiguration: JobConfiguration, value: JobConfiguration, formatValue: string): JobConfiguration {
        let { format } = value;
        if (format === Format.custom) {
            format = formatValue;
        }

        const parameters = value.parameters.map(parameter => ({
            name: parameter.name,
            value: parameter.value
        }));

        return { idJob: jobConfiguration.idJob, idDGE: jobConfiguration.idDGE, format, project: value.project, output: value.output, parameters };
    }

    private onChangeFormat(form: DpModalForm): void {

        form.formGroup?.get('format')?.valueChanges.subscribe(() => {
            if (form.formGroup?.get('format')?.value === Format.custom) {
                form.formGroup?.get('formatValue')?.enable();
                const formatValueField = form.getField('formatValue') as DpFieldText;
                formatValueField.disabled = false;

            } else {
                form.formGroup?.get('formatValue')?.disable();
                const formatValueField = form.getField('formatValue') as DpFieldText;
                formatValueField.disabled = true;
            }
        });
    }

    private getRelaunchJobFormButtons(): DpFormButton[] {
        return [
            new DpFormButton({ type: DpFormButtonType.Cancel }),
            new DpFormButton({ type: DpFormButtonType.Submit, label: 'statistics.relaunch-form.button-relaunch', enabled: true })
        ];
    }

    private relaunchJob(jobConfiguration: JobConfiguration, activeModal: NgbModalRef) {
        this.statisticsHttpService.relaunchJob(jobConfiguration).subscribe(() => {
            activeModal.close();
            this.statisticsService.notifyTableChange();
        });
    }

    private getFormatField(): DpFieldSelect {
        const options: DpSelectOption[] = [];

        Object.values(Format).forEach(format => {
            options.push(new DpSelectOption({ value: format, text: format }));
        });

        return new DpFieldSelect({
            name: 'format',
            options,
            column: 5
        });
    }

    private getParametersFields(): DpFormRow {
        return new DpFormRow([
            new DpFieldTableGroup({
                name: 'parameters',
                column: 12,
                labelColumn: 6,
                maxHeight: '31vh',
                validateFirstFieldUnique: true,
                columns: [
                    new DpTableGroupColumn({ field: new DpFieldText({ name: 'name' }) }),
                    new DpTableGroupColumn({ field: new DpFieldText({ name: 'value' }) })
                ]
            })
        ]);
    }

    private initViewLaunchedJobsForm(relaunchedJobs: Job[]): DpModalForm {
        return new DpModalForm({
            header: `${this.i18nPrefixRelaunched}.header.relaunched-jobs`,
            rows: [new DpFormRow([
                new DpFieldTable({
                    name: 'relaunchedJobs',
                    column: 12,
                    labelHidden: true,
                    table: this.initRelaunchedJobsTable(relaunchedJobs),
                    heightScroll: '20vh'
                })
            ])],
            size: DpModalFormSize.Xl,
            i18nPrefix: this.i18nPrefixRelaunched,
            buttons: [new DpFormButton({ type: DpFormButtonType.Cancel, label: 'ext.search.loadModal.close' })]
        });
    }

    private loadHeaderTitle(idJob: number): string {
        let modalTitle = '';
        const parameterObjects: { [key: string]: string; } = {};
        parameterObjects[`param${0}`] = idJob.toString();
        this.translateService.get(`${this.i18nPrefixRelaunched}.header.relaunched-jobs`, parameterObjects).subscribe(
            (title: string) => {
                modalTitle = title;
            }
        );
        return modalTitle;
    }

    private initRelaunchedJobsTable(relaunchedJobs: Job[]): Table {
        const header = this.initRelaunchedJobsTableHeaders(),
            body = this.initRelaunchedJobsTableBody(relaunchedJobs);

        return new Table(header, body);
    }

    private initRelaunchedJobsTableBody(relaunchedJobs: Job[]): TableRow[] {
        const style = 'text-align:center';
        return relaunchedJobs.map(job => {
            const fields: TableField[] = [],
                { idJob, dge, documents, pages, formName, started, finished, queueTime, executionTime, tag1, tag2, tag3, tag4, tag5, status, transactions, preview } = job;

            fields.push(new TableFieldText(idJob.toString(), undefined, { style: 'font-weight: bold' }, undefined, '8%'));
            fields.push(new TableFieldText(dge, undefined, undefined, dge, '6%'));
            if (!(this.cloudService.isModeCloud() && this.sessionCookieService.getUserLogged()?.role === Constants.ROLE_NORMAL)) {
                fields.push(new TableFieldText(dge, undefined, undefined, dge, '6%'));
            }
            if (this.cloudService.isModeCloud()) {
                fields.push(new TableFieldText(transactions.toString(), undefined, { style }, undefined, '5%'));
            } else {
                fields.push(new TableFieldText(documents.toString(), undefined, { style }, undefined, '5%'));
            }
            fields.push(new TableFieldText(pages.toString(), undefined, { style }, undefined, '5%'));
            fields.push(new TableFieldText(formName, undefined, undefined, formName, '9%'));
            fields.push(new TableFieldDate(started, this.utilService.getDateFormats(), undefined, undefined, { style }, '10%', undefined));
            fields.push(new TableFieldDate(finished, this.utilService.getDateFormats(), undefined, undefined, { style }, '10%', undefined));
            fields.push(new TableFieldText(`${queueTime.toString()} ms`, undefined, { style }, undefined, '5%'));
            fields.push(new TableFieldText(`${executionTime.toString()} ms`, undefined, { style }, undefined, '5%'));
            fields.push(new TableFieldText(tag1, undefined, { style }, tag1, '5%'));
            fields.push(new TableFieldText(tag2, undefined, { style }, tag2, '5%'));
            fields.push(new TableFieldText(tag3, undefined, { style }, tag3, '5%'));
            fields.push(new TableFieldText(tag4, undefined, { style }, tag4, '5%'));
            fields.push(new TableFieldText(tag5, undefined, { style }, tag5, '5%'));
            fields.push(this.statisticsTableService.getStatusIcons(status));
            fields.push(new TableFieldText(preview, undefined, { style }, preview, '5%'));

            const tableRow = new TableRow(fields);
            tableRow.content = job;
            return tableRow;
        });
    }

    private initRelaunchedJobsTableHeaders(): TableRow {
        const style = 'text-align:center';
        const head = new TableRow([]);
        
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.id`, undefined, undefined, `${this.i18nPrefixRelaunched}.id`, '8%', true));
        if (!(this.cloudService.isModeCloud() && this.sessionCookieService.getUserLogged()?.role === Constants.ROLE_NORMAL)) {
            head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.table.dge`, undefined, undefined, `${this.i18nPrefixRelaunched}.table.dge`, '6%', true));
        }
        head.fields.push(new TableFieldText(
            this.cloudService.isModeCloud() ? `${this.i18nPrefixRelaunched}.table.transactions` : `${this.i18nPrefixRelaunched}.table.documents`,
            undefined,
            { style },
            this.cloudService.isModeCloud() ? `${this.i18nPrefixRelaunched}.table.transactions` : `${this.i18nPrefixRelaunched}.table.documents`,
            '5%',
            true
        ));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.pages`, undefined, { style }, `${this.i18nPrefixRelaunched}.pages`, '5%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.form-name`, undefined, undefined, `${this.i18nPrefixRelaunched}.form-name`, '9%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.started`, undefined, { style }, `${this.i18nPrefixRelaunched}.started`, '10%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.finished`, undefined, { style }, `${this.i18nPrefixRelaunched}.finished`, '10%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.queue-time`, undefined, { style }, `${this.i18nPrefixRelaunched}.queue-time`, '5%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.execution-time`, undefined, { style }, `${this.i18nPrefixRelaunched}.execution-time`, '5%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.tag1`, undefined, { style }, `${this.i18nPrefixRelaunched}.tag1`, '5%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.tag2`, undefined, { style }, `${this.i18nPrefixRelaunched}.tag2`, '5%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.tag3`, undefined, { style }, `${this.i18nPrefixRelaunched}.tag3`, '5%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.tag4`, undefined, { style }, `${this.i18nPrefixRelaunched}.tag4`, '5%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.tag5`, undefined, { style }, `${this.i18nPrefixRelaunched}.tag5`, '5%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.status.label`, undefined, { style }, `${this.i18nPrefixRelaunched}.status.label`, '7%', true));
        head.fields.push(new TableFieldText(`${this.i18nPrefixRelaunched}.preview.label`, undefined, { style }, `${this.i18nPrefixRelaunched}.preview.label`, '7%', true));

        head.disableSelected = true;

        return head;
    }

}
